import { z } from 'zod';
import { mapRecord } from 'libs/end-to-end/src/arrays';
import { BiasType } from 'libs/end-to-end/src/bias-type';
import { FocusArea } from 'libs/end-to-end/src/focus-area';

export const TestStatus = z
  .enum(['Incomplete', 'Fail', 'Pass'])
  .describe('TestStatus');
export type TestStatus = z.infer<typeof TestStatus>;
export const TestStatuses = Object.values(TestStatus.Values);

export const TestStatusIds: Record<TestStatus, number> = {
  Incomplete: -1,
  Fail: 0,
  Pass: 1,
};

export const TestVideoStatus = z
  .enum(['unavailable', 'comingSoon', 'available'])
  .describe('TestVideoStatus');

export const Test = z
  .object({
    focusArea: FocusArea,
    biasType: BiasType,
    dimension: z.string(),
    metric: z.string(),
    level: z.string(),
    status: TestStatus,
    videoUrl: z.string().optional(),
    videoThumbnailUrl: z.string().optional(),
    videoStatus: TestVideoStatus,
  })
  .describe('Test');

export type Test = z.infer<typeof Test>;

export const TestCount = z
  .object({
    passing: z.number(),
    failing: z.number(),
    incomplete: z.number(),
  })
  .describe('TestCount');

export type TestCount = z.infer<typeof TestCount>;

// IMPORTANT: We exclude incomplete tests from the total test count for the sake of calculations.
export const getTotalTestCount = ({
  passing,
  failing,
}: {
  passing: number;
  failing: number;
}) => passing + failing;

export const TestSummary = z
  .object({
    tests: z.array(Test),
    counts: TestCount,
  })
  .describe('TestSummary');

export type TestSummary = z.infer<typeof TestSummary>;

export const getTestCounts = (tests: Test[]): TestCount => {
  let passing = 0;
  let failing = 0;
  let incomplete = 0;

  for (const test of tests) {
    switch (test.status) {
      case 'Pass':
        passing++;
        break;
      case 'Fail':
        failing++;
        break;
      case 'Incomplete':
        incomplete++;
        break;
    }
  }

  return { passing, failing, incomplete };
};

export const getTestCountsByFocusArea = (
  tests: Test[],
  focusArea: FocusArea,
): TestCount => {
  let passing = 0;
  let failing = 0;
  let incomplete = 0;

  for (const test of tests) {
    if (test.focusArea === focusArea) {
      switch (test.status) {
        case 'Pass':
          passing++;
          break;
        case 'Fail':
          failing++;
          break;
        case 'Incomplete':
          incomplete++;
          break;
      }
    }
  }

  return { passing, failing, incomplete };
};

export const TestCountsForDimension = z.record(
  z.string(),
  z.object({
    passing: z.number(),
    failing: z.number(),
    incomplete: z.number(),
    levels: z.record(TestCount),
  }),
);

export type TestCountsForDimension = z.infer<typeof TestCountsForDimension>;

export const TestCountsForBiasType = z.object({
  passing: z.number(),
  failing: z.number(),
  incomplete: z.number(),
  dimensions: TestCountsForDimension,
});

export type TestCountsForBiasType = z.infer<typeof TestCountsForBiasType>;

export const getTestCountsByBiasTypeAndDimension = (
  tests: Test[],
): Record<BiasType, TestCountsForBiasType> => {
  const counts: Record<BiasType, TestCountsForBiasType> = mapRecord(
    BiasType,
    () => ({
      passing: 0,
      failing: 0,
      incomplete: 0,
      dimensions: {},
    }),
  );

  for (const test of tests) {
    if (!(test.dimension in counts[test.biasType].dimensions)) {
      counts[test.biasType].dimensions[test.dimension] = {
        passing: 0,
        failing: 0,
        incomplete: 0,
        levels: {},
      };
    }

    if (
      !(test.level in counts[test.biasType].dimensions[test.dimension].levels)
    ) {
      counts[test.biasType].dimensions[test.dimension].levels[test.level] = {
        passing: 0,
        failing: 0,
        incomplete: 0,
      };
    }

    switch (test.status) {
      case 'Pass':
        counts[test.biasType].passing++;
        counts[test.biasType].dimensions[test.dimension].passing++;
        counts[test.biasType].dimensions[test.dimension].levels[test.level]
          .passing++;
        counts[test.biasType].dimensions[test.dimension].levels[test.level];
        break;
      case 'Fail':
        counts[test.biasType].failing++;
        counts[test.biasType].dimensions[test.dimension].failing++;
        counts[test.biasType].dimensions[test.dimension].levels[test.level]
          .failing++;
        counts[test.biasType].dimensions[test.dimension].levels[test.level];
        break;
      case 'Incomplete':
        counts[test.biasType].incomplete++;
        counts[test.biasType].dimensions[test.dimension].incomplete++;
        counts[test.biasType].dimensions[test.dimension].levels[test.level]
          .incomplete++;
        break;
    }
  }

  return counts;
};

export const getTestCountsByDimension = (
  tests: Test[],
  dimension: string,
): TestCountsForDimension => {
  const counts: TestCountsForDimension = {};

  for (const test of tests) {
    const lowerDimension = test.dimension.toLowerCase();
    if (lowerDimension === dimension.toLowerCase()) {
      if (!counts[lowerDimension]) {
        counts[lowerDimension] = {
          passing: 0,
          failing: 0,
          incomplete: 0,
          levels: {},
        };
      }

      const lowerLevel = test.level.toLowerCase();
      if (!(lowerLevel in counts[lowerDimension].levels)) {
        counts[lowerDimension].levels[lowerLevel] = {
          passing: 0,
          failing: 0,
          incomplete: 0,
        };
      }

      switch (test.status) {
        case 'Pass':
          counts[lowerDimension].passing++;
          counts[lowerDimension].levels[lowerLevel].passing++;
          break;
        case 'Fail':
          counts[lowerDimension].failing++;
          counts[lowerDimension].levels[lowerLevel].failing++;
          break;
        case 'Incomplete':
          counts[lowerDimension].incomplete++;
          counts[lowerDimension].levels[lowerLevel].incomplete++;
          break;
      }
    }
  }

  return counts;
};

export const getCalibrationScore = ({
  testCount,
}: {
  testCount: TestCount;
}): number => {
  const total = getTotalTestCount(testCount);
  // The getTotalTestCount takes into account only passing and failing tests added together.
  // The default case if all tests are incomplete should be the highest calibration score, which
  // would show low bias or high optimization.
  if (total === 0) {
    return 6;
  }
  return 6 * (testCount.passing / total);
};

export const determineTestsForFocusAreaSummaryConsideringFilters = ({
  focusArea,
  metric,
  testSummary,
}: {
  testSummary: TestSummary | null;
  focusArea: string;
  metric: string | null;
}) => {
  return (
    testSummary?.tests.filter(
      (test) =>
        test.focusArea === focusArea &&
        (metric === null || test.metric === metric),
    ) ?? []
  );
};
